import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getTenantMe,
  TenantMeData,
  ApplicantMeData,
  ProspectMeData,
  type PortalMeData,
} from '../services/tenantApi';
import { notifyTenantPortalAuthFailure } from '../services/tenantPortalSession';

const API_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE || undefined;

interface TenantProfileState {
  kind: PortalMeData['kind'] | null;
  tenant: TenantMeData | null;
  applicant: ApplicantMeData | null;
  prospect: ProspectMeData | null;
  loading: boolean;
  error: string | null;
  isNotInSystem: boolean;
  refreshProfile: () => Promise<void>;
}

const emptyState: Omit<TenantProfileState, 'refreshProfile'> = {
  kind: null,
  tenant: null,
  applicant: null,
  prospect: null,
  loading: true,
  error: null,
  isNotInSystem: false,
};

const TenantProfileContext = createContext<TenantProfileState>({
  ...emptyState,
  refreshProfile: async () => {},
});

const TokenContext = createContext<() => Promise<string>>(() => Promise.reject(new Error('No token provider')));

export function useTenantProfile() {
  return useContext(TenantProfileContext);
}

export function useTenantApiToken(): () => Promise<string> {
  return useContext(TokenContext);
}

function getTokenForApi(getAccessTokenSilently: () => Promise<string>): Promise<string> {
  if (API_AUDIENCE) {
    return getAccessTokenSilently({ authorizationParams: { audience: API_AUDIENCE } });
  }
  return getAccessTokenSilently();
}

function normalizeProspectMe(data: ProspectMeData): ProspectMeData {
  return {
    ...data,
    tertiaryBedroom: data.tertiaryBedroom ?? null,
    generalNotifications: data.generalNotifications ?? false,
  };
}

function splitPortalMe(data: PortalMeData): Pick<TenantProfileState, 'kind' | 'tenant' | 'applicant' | 'prospect'> {
  if (data.kind === 'applicant') {
    return { kind: 'applicant', tenant: null, applicant: data, prospect: null };
  }
  if (data.kind === 'prospect') {
    return {
      kind: 'prospect',
      tenant: null,
      applicant: null,
      prospect: normalizeProspectMe(data),
    };
  }
  return { kind: 'tenant', tenant: data, applicant: null, prospect: null };
}

export function TenantProfileProvider({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [kind, setKind] = useState<PortalMeData['kind'] | null>(null);
  const [tenant, setTenant] = useState<TenantMeData | null>(null);
  const [applicant, setApplicant] = useState<ApplicantMeData | null>(null);
  const [prospect, setProspect] = useState<ProspectMeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isNotInSystem, setIsNotInSystem] = useState(false);
  const fetchingRef = useRef(false);

  const getToken = useCallback(() => getTokenForApi(getAccessTokenSilently), [getAccessTokenSilently]);

  const applyMe = useCallback((data: PortalMeData) => {
    const next = splitPortalMe(data);
    setKind(next.kind);
    setTenant(next.tenant);
    setApplicant(next.applicant);
    setProspect(next.prospect);
    setIsNotInSystem(false);
    setError(null);
  }, []);

  const load = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await getTenantMe(getToken);
      applyMe(res.data);
    } catch (err) {
      setKind(null);
      setTenant(null);
      setApplicant(null);
      setProspect(null);
      setIsNotInSystem(true);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
      notifyTenantPortalAuthFailure('profile_load_failed');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [getToken, applyMe]);

  useEffect(() => {
    if (isAuthenticated) {
      load();
    }
  }, [isAuthenticated, load]);

  const refreshProfile = useCallback(async () => {
    try {
      const res = await getTenantMe(getToken);
      applyMe(res.data);
    } catch (err) {
      notifyTenantPortalAuthFailure('profile_load_failed');
      throw err;
    }
  }, [getToken, applyMe]);

  return (
    <TokenContext.Provider value={getToken}>
      <TenantProfileContext.Provider
        value={{ kind, tenant, applicant, prospect, loading, error, isNotInSystem, refreshProfile }}
      >
        {children}
      </TenantProfileContext.Provider>
    </TokenContext.Provider>
  );
}

export function ImpersonationProfileProvider({ token, children }: { token: string; children: React.ReactNode }) {
  const [kind, setKind] = useState<PortalMeData['kind'] | null>(null);
  const [tenant, setTenant] = useState<TenantMeData | null>(null);
  const [applicant, setApplicant] = useState<ApplicantMeData | null>(null);
  const [prospect, setProspect] = useState<ProspectMeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const getToken = useCallback(() => Promise.resolve(token), [token]);

  const applyMe = useCallback((data: PortalMeData) => {
    const next = splitPortalMe(data);
    setKind(next.kind);
    setTenant(next.tenant);
    setApplicant(next.applicant);
    setProspect(next.prospect);
    setError(null);
  }, []);

  const load = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      setLoading(true);
      const res = await getTenantMe(getToken);
      applyMe(res.data);
    } catch (err) {
      setKind(null);
      setTenant(null);
      setApplicant(null);
      setProspect(null);
      setError(err instanceof Error ? err.message : 'Failed to load tenant profile');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [getToken, applyMe]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshProfile = useCallback(async () => {
    const res = await getTenantMe(getToken);
    applyMe(res.data);
  }, [getToken, applyMe]);

  return (
    <TokenContext.Provider value={getToken}>
      <TenantProfileContext.Provider
        value={{
          kind,
          tenant,
          applicant,
          prospect,
          loading,
          error,
          isNotInSystem: false,
          refreshProfile,
        }}
      >
        {children}
      </TenantProfileContext.Provider>
    </TokenContext.Provider>
  );
}
